<template>
  <transition name="bg-game-frame">
    <div
      v-if="visible && frameUrl"
      class="bg-game-frame-overlay"
      role="presentation"
    >
      <div
        ref="panel"
      class="bg-game-frame"
      role="dialog"
      aria-modal="true"
      :aria-label="displayTitle"
      tabindex="-1"
      @keydown.esc.prevent="close"
    >
        <header class="bg-game-frame__header">
          <div class="bg-game-frame__brand">
            <span class="bg-game-frame__mark" aria-hidden="true" />
            <div class="bg-game-frame__titles">
              <p class="bg-game-frame__eyebrow">{{ categoryLabel }}</p>
              <h2 class="bg-game-frame__title">{{ displayTitle }}</h2>
            </div>
          </div>

          <div class="bg-game-frame__actions">
            <span
              v-if="mode === 'demo'"
              class="bg-game-frame__pill bg-game-frame__pill--demo"
            >{{ $t('games.demo') }}</span>
            <span
              v-else
              class="bg-game-frame__pill bg-game-frame__pill--real"
            >{{ $t('games.play') }}</span>

            <button
              type="button"
              class="bg-game-frame__icon-btn"
              :aria-label="fullscreen ? $t('games.frameExitFullscreen') : $t('games.frameFullscreen')"
              @click="toggleFullscreen"
            >
              <svg v-if="!fullscreen" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  d="M9 9H5V5M15 9h4V5M9 15H5v4M15 15h4v4"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <button
              type="button"
              class="bg-game-frame__icon-btn bg-game-frame__icon-btn--close"
              :aria-label="$t('common.close')"
              @click="close"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </button>
          </div>
        </header>

        <div ref="body" class="bg-game-frame__body">
          <div v-if="frameLoading" class="bg-game-frame__loader" aria-live="polite">
            <span class="bg-game-frame__loader-ring" aria-hidden="true" />
            <p>{{ $t('games.frameLoading') }}</p>
          </div>

          <iframe
            v-if="activeUrl"
            ref="frame"
            class="bg-game-frame__iframe"
            :src="activeUrl"
            :title="displayTitle"
            frameborder="0"
            scrolling="auto"
            allow="autoplay; fullscreen; payment"
            referrerpolicy="no-referrer-when-downgrade"
            @load="onFrameLoad"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { resolveGameCategoryLabel } from '@/utils/game'

export default {
  name: 'BlackGoldGameFrameModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    frameUrl: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    mode: {
      type: String,
      default: 'real'
    },
    category: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      activeUrl: '',
      frameLoading: true,
      fullscreen: false
    }
  },
  computed: {
    displayTitle () {
      return this.title || this.$t('games.frameTitle')
    },
    categoryLabel () {
      return resolveGameCategoryLabel(this.category, key => this.$t(key))
    }
  },
  watch: {
    visible (open) {
      if (open && this.frameUrl) {
        this.frameLoading = true
        this.activeUrl = this.frameUrl
        this.$nextTick(() => {
          const panel = this.$refs.panel
          if (panel && panel.focus) panel.focus()
        })
        return
      }
      this.resetFrame()
    },
    frameUrl (url) {
      if (this.visible && url) {
        this.frameLoading = true
        this.activeUrl = url
      }
    }
  },
  mounted () {
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
  },
  beforeDestroy () {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    this.exitFullscreen()
    this.resetFrame()
  },
  methods: {
    close () {
      this.exitFullscreen()
      this.$emit('close')
    },
    onFrameLoad () {
      this.frameLoading = false
    },
    resetFrame () {
      this.frameLoading = true
      this.activeUrl = ''
    },
    toggleFullscreen () {
      if (this.fullscreen) {
        this.exitFullscreen()
        return
      }
      const target = this.$refs.body
      if (!target || !target.requestFullscreen) return
      target.requestFullscreen().catch(() => {})
    },
    exitFullscreen () {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {})
      }
    },
    onFullscreenChange () {
      this.fullscreen = document.fullscreenElement === this.$refs.body
    }
  }
}
</script>
